import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ShoppingBag, Download } from "lucide-react";
import api, { formatNGN, API } from "../lib/api";

export default function Checkout() {
    const navigate = useNavigate();
    const [cart, setCart] = useState([]);
    const [form, setForm] = useState({ customer_name: "", customer_email: "", customer_phone: "", shipping_address: "" });
    const [paymentMethod, setPaymentMethod] = useState("paystack");
    const [submitting, setSubmitting] = useState(false);
    const [confirmation, setConfirmation] = useState(null);
    const [downloads, setDownloads] = useState([]);

    useEffect(() => {
        const raw = sessionStorage.getItem("rj_cart");
        if (raw) setCart(JSON.parse(raw));
    }, []);

    const total = cart.reduce((s, i) => s + i.unit_price * i.quantity, 0);
    const hasHardcopy = cart.some((i) => i.format === "hardcopy");
    const hasSoftcopy = cart.some((i) => i.format === "softcopy");

    const placeOrder = async (e) => {
        e.preventDefault();
        if (!cart.length) return toast.error("Your cart is empty");
        setSubmitting(true);
        try {
            const { data } = await api.post("/orders", {
                ...form,
                items: cart.map((c) => ({ publication_id: c.publication_id, quantity: c.quantity, format: c.format })),
            });
            if (paymentMethod === "manual") {
                setConfirmation(data.order);
                sessionStorage.removeItem("rj_cart");
                toast.success("Order placed. Complete payment via bank transfer and admin will confirm manually.");
            } else {
                const confirmed = await api.post(`/orders/${data.order.id}/confirm`);
                setConfirmation(confirmed.data);
                sessionStorage.removeItem("rj_cart");
                if (hasSoftcopy) {
                    try {
                        const dl = await api.get(`/orders/${confirmed.data.id}/downloads`, {
                            params: { ref: confirmed.data.payment_ref, email: confirmed.data.customer_email },
                        });
                        setDownloads(dl.data.downloads || []);
                    } catch {}
                }
                toast.success("Payment confirmed (mocked) — your order is placed.");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not place order");
        } finally {
            setSubmitting(false);
        }
    };

    if (confirmation) {
        const isPaid = confirmation.status === "paid";
        return (
            <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="checkout-success">
                <CheckCircle2 className="w-16 h-16 text-[#c5a059] mx-auto" />
                <h1 className="font-serif-display text-4xl mt-6">{isPaid ? "Order confirmed" : "Order pending payment"}</h1>
                <p className="mt-3 text-slate-700">
                    Reference <span className="font-mono-cap text-xs">{confirmation.payment_ref}</span> — {isPaid ? "a receipt has been emailed to" : "please send proof of payment to"} {confirmation.customer_email}.
                </p>
                {!isPaid && (
                    <div className="mt-8 border border-slate-900/10 p-6 bg-[#fff8e1] text-left text-slate-700">
                        <div className="font-semibold text-slate-900 mb-3">Manual payment instructions</div>
                        <div>Bank: Access Bank</div>
                        <div>Account name: Rotimi Jacobs &amp; Co.</div>
                        <div>Account number: 1725603532</div>
                        <div className="mt-3 text-sm text-slate-600">
                            Please include your order reference in the transfer narration. Admin will confirm your payment manually once received.
                        </div>
                    </div>
                )}
                <div className="mt-8 border border-slate-900/10 p-6 text-left bg-[#faf9f6]">
                    {confirmation.items.map((i, idx) => (
                        <div key={idx} className="flex justify-between text-sm py-2 border-b last:border-0 border-slate-900/10">
                            <div>{i.title} <span className="text-xs text-slate-500">({i.format} × {i.quantity})</span></div>
                            <div className="font-mono-cap">{formatNGN(i.line_total)}</div>
                        </div>
                    ))}
                    <div className="flex justify-between mt-3 pt-3 border-t border-slate-900/10 font-medium">
                        <div>Total</div>
                        <div>{formatNGN(confirmation.total_ngn)}</div>
                    </div>
                </div>
                {isPaid && downloads.length > 0 && (
                    <div className="mt-8 border border-[#c5a059] p-6 text-left bg-white" data-testid="checkout-downloads">
                        <div className="font-mono-cap text-[10px] text-[#c5a059] mb-3">Digital Downloads</div>
                        <ul className="space-y-2">
                            {downloads.map((d, i) => (
                                <li key={i} className="flex items-center justify-between gap-3 text-sm">
                                    <span className="text-slate-800">{d.title}</span>
                                    {d.url ? (
                                        <a
                                            href={`${API.replace(/\/api$/, "")}${d.url}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            data-testid={`checkout-download-${i}`}
                                            className="inline-flex items-center gap-2 bg-[#0F172A] text-[#faf9f6] px-4 py-2 rounded-sm hover:bg-[#c5a059] hover:text-[#0F172A] transition-colors"
                                        >
                                            <Download className="w-4 h-4" /> Download PDF
                                        </a>
                                    ) : (
                                        <span className="text-xs text-slate-500">PDF will be emailed when ready</span>
                                    )}
                                </li>
                            ))}
                        </ul>
                        <p className="mt-4 text-xs text-slate-500">Links are valid for 24 hours. A copy has been sent to your email.</p>
                    </div>
                )}
                <Link to="/publications" className="inline-block mt-8 rj-link text-sm font-medium">Continue browsing →</Link>
            </div>
        );
    }

    return (
        <div data-testid="page-checkout">
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="font-mono-cap text-xs text-[#c5a059] mb-3">Checkout</div>
                <h1 className="font-serif-display text-4xl md:text-5xl">Complete your order.</h1>
            </section>
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 grid grid-cols-1 md:grid-cols-12 gap-10">
                <form onSubmit={placeOrder} className="md:col-span-7 space-y-5" data-testid="checkout-form">
                    <div className="grid grid-cols-2 gap-3">
                        <button
                            type="button"
                            className={`rounded-sm py-3 text-sm font-medium ${paymentMethod === "paystack" ? "bg-[#0F172A] text-[#faf9f6]" : "border border-slate-900/10 text-slate-900 bg-white"}`}
                            onClick={() => setPaymentMethod("paystack")}
                        >
                            Paystack
                        </button>
                        <button
                            type="button"
                            className={`rounded-sm py-3 text-sm font-medium ${paymentMethod === "manual" ? "bg-[#0F172A] text-[#faf9f6]" : "border border-slate-900/10 text-slate-900 bg-white"}`}
                            onClick={() => setPaymentMethod("manual")}
                        >
                            Manual Bank Transfer
                        </button>
                    </div>
                    <p className="text-xs text-slate-500">
                        Choose manual payment to transfer directly into the firm's bank account. Admin will confirm your payment manually once received.
                    </p>
                    <Field label="Full name" required value={form.customer_name} onChange={(v) => setForm({ ...form, customer_name: v })} testid="checkout-name" />
                    <Field label="Email" type="email" required value={form.customer_email} onChange={(v) => setForm({ ...form, customer_email: v })} testid="checkout-email" />
                    <Field label="Phone" required value={form.customer_phone} onChange={(v) => setForm({ ...form, customer_phone: v })} testid="checkout-phone" />
                    {hasHardcopy && (
                        <Field
                            label="Shipping address (for hardcover)"
                            required
                            textarea
                            value={form.shipping_address}
                            onChange={(v) => setForm({ ...form, shipping_address: v })}
                            testid="checkout-address"
                        />
                    )}
                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="checkout-submit"
                        className="w-full bg-[#0F172A] text-[#faf9f6] py-3.5 rounded-sm font-medium hover:bg-[#c5a059] hover:text-[#0F172A] transition-colors disabled:opacity-50"
                    >
                        {submitting ? "Processing payment…" : paymentMethod === "manual" ? `Place order and pay manually` : `Pay ${formatNGN(total)} via Paystack`}
                    </button>
                    <p className="text-xs text-slate-500">
                        Payment is mocked in this preview. Live Paystack integration will activate once the merchant
                        keys are provisioned.
                    </p>
                </form>

                <aside className="md:col-span-5">
                    <div className="border border-slate-900/10 bg-[#faf9f6] p-7" data-testid="checkout-summary">
                        <div className="font-mono-cap text-xs text-[#c5a059] mb-4 flex items-center gap-2"><ShoppingBag className="w-3.5 h-3.5"/> Order summary</div>
                        {cart.length === 0 ? (
                            <p className="text-sm text-slate-500">Your cart is empty. <Link to="/publications" className="rj-link">Browse publications</Link>.</p>
                        ) : (
                            <>
                                {cart.map((i, idx) => (
                                    <div key={idx} className="flex gap-3 py-3 border-b last:border-0 border-slate-900/10">
                                        <img src={i.cover_url} alt={i.title} className="w-14 h-20 object-cover" />
                                        <div className="flex-1">
                                            <div className="font-serif-display text-base">{i.title}</div>
                                            <div className="text-xs text-slate-500 capitalize">{i.format} · qty {i.quantity}</div>
                                        </div>
                                        <div className="font-mono-cap text-xs">{formatNGN(i.unit_price * i.quantity)}</div>
                                    </div>
                                ))}
                                <div className="mt-4 pt-3 border-t border-slate-900/10 flex items-center justify-between">
                                    <div className="font-serif-display text-lg">Total</div>
                                    <div className="font-serif-display text-xl">{formatNGN(total)}</div>
                                </div>
                            </>
                        )}
                    </div>
                </aside>
            </section>
        </div>
    );
}

const Field = ({ label, value, onChange, required, type = "text", textarea, testid }) => (
    <label className="block">
        <span className="font-mono-cap text-[10px] text-slate-500 block mb-2">{label}{required && " *"}</span>
        {textarea ? (
            <textarea
                value={value}
                onChange={(e) => onChange(e.target.value)}
                required={required}
                rows={3}
                data-testid={testid}
                className="w-full px-4 py-3 border border-slate-900/15 rounded-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#00a3e0]"
            />
        ) : (
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                required={required}
                data-testid={testid}
                className="w-full px-4 py-3 border border-slate-900/15 rounded-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#00a3e0]"
            />
        )}
    </label>
);
